// ===================================================================
// THEME TOGGLE - Design System Portfolio
// ===================================================================
// Bouton de basculement light/dark simple (meilleure UX)
// Accessibilité : aria-label, focus visible, keyboard navigation
// ===================================================================

'use client';

import React from 'react';
import { useTheme } from '../ThemeProvider';

export interface ThemeToggleProps {
  /** Classes CSS additionnelles */
  className?: string;
  /** Afficher le label textuel */
  showLabel?: boolean;
  /** Taille du bouton */
  size?: 'sm' | 'md' | 'lg';
}

// Icônes SVG inline pour éviter les dépendances
const SunIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M12 3v2.25m6.364.386l-1.591 1.591M21 12h-2.25m-.386 6.364l-1.591-1.591M12 18.75V21m-4.773-4.227l-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0z"
    />
  </svg>
);

const MoonIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M21.752 15.002A9.718 9.718 0 0118 15.75c-5.385 0-9.75-4.365-9.75-9.75 0-1.33.266-2.597.748-3.752A9.753 9.753 0 003 11.25C3 16.635 7.365 21 12.75 21a9.753 9.753 0 009.002-5.998z"
    />
  </svg>
);

// Tailles du bouton (touch target >= 44px pour accessibilité)
const sizeClasses = {
  sm: 'p-2 min-w-[44px] min-h-[44px]',
  md: 'p-2.5 min-w-[44px] min-h-[44px]',
  lg: 'p-3 min-w-[48px] min-h-[48px]',
};

const iconSizeClasses = {
  sm: 'w-5 h-5',
  md: 'w-5 h-5',
  lg: 'w-6 h-6',
};

export function ThemeToggle({
  className = '',
  showLabel = false,
  size = 'md',
}: ThemeToggleProps) {
  const { resolvedTheme, toggleTheme } = useTheme();

  // En dark mode on montre le soleil (pour passer en light)
  // En light mode on montre la lune (pour passer en dark)
  const isDark = resolvedTheme === 'dark';
  const Icon = isDark ? SunIcon : MoonIcon;
  const ariaLabel = isDark ? 'Passer au thème clair' : 'Passer au thème sombre';

  return (
    <button
      type="button"
      onClick={toggleTheme}
      aria-label={ariaLabel}
      aria-pressed={isDark}
      className={`
        inline-flex items-center justify-center gap-2
        ${sizeClasses[size]}
        rounded-lg
        text-slate-600 dark:text-slate-300
        bg-slate-100 dark:bg-slate-800
        hover:bg-slate-200 dark:hover:bg-slate-700
        focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:ring-offset-2
        dark:focus-visible:ring-offset-slate-900
        transition-colors duration-200
        ${className}
      `}
    >
      <Icon className={`${iconSizeClasses[size]} transition-transform`} />
      {showLabel && (
        <span className="text-sm font-medium">
          {isDark ? 'Clair' : 'Sombre'}
        </span>
      )}
    </button>
  );
}

export default ThemeToggle;
